/**
 * Reads from, and writes back to, the legacy native `<select>` / `<ul>` an eCOS list field is
 * mounted over (ticket #23442). See `public/m.eagle/app/components/sales/visitStep3-statement.js`
 * `getAttributeField` (`case 4` / `case 5`).
 *
 * The native element is kept as the single source of truth on purpose: the relevé already has a
 * save path (`.attribChange` → `__attributeChanged`), a mandatory-validation lookup
 * (`$('#atr<id>_<pdt>')`), a read-only path (`.prop('disabled', true)`) and a wizard↔list DOM sync,
 * all of which key off that element. Driving it instead of replacing it means none of that legacy
 * code has to change — see the ticket's plan for why the alternative (Vue owns the value and calls
 * a new save API) was rejected.
 */

// `getAttribute()` is case-insensitive for HTML elements per the DOM spec (it lowercases the name
// before matching), so `data-atrType` — as the legacy actually authors it — resolves correctly
// here. `.dataset.atrType` would NOT: `.dataset` maps the attribute's stored (lowercased) name
// `data-atrtype` to `dataset.atrtype`, with no way back to the original camelCase.
/** @param {Element} el @returns {number} */
function attributeType(el) {
	return Number(el.getAttribute('data-atrType') ?? NaN);
}

/**
 * @param {HTMLElement} nativeEl the `<select>` (type 4) or `<ul>` (type 5)
 * @returns {5|4|null} `null` when the element is neither — callers should treat that as "not ours"
 */
export function nativeFieldType(nativeEl) {
	const type = attributeType(nativeEl);
	if (type === 4 || type === 5) return type;
	return null;
}

/**
 * Reads the current state straight off the native element.
 *
 * Deliberately does not read from Vue-owned state: `makeReadOnly` (`.prop('disabled', true)`) and
 * the wizard↔list sync (`.val()` / `.prop('checked', …)`) mutate PROPERTIES, which fire no
 * mutation event and leave no trace for a `MutationObserver` to pick up — the DOM is the only place
 * this can be read from correctly. See `syncFromNative` callers for the explicit-refresh + sweep
 * reconciliation this implies.
 *
 * @param {HTMLElement} nativeEl
 * @returns {{ type: 4|5, options: Array<{id: string, label: string}>, selected: string[], disabled: boolean, mandatory: boolean } | null}
 */
export function readNativeField(nativeEl) {
	const type = nativeFieldType(nativeEl);
	if (type === null) return null;

	const mandatory = nativeEl.classList.contains('mandatory') || nativeEl.classList.contains('input-mandatory');

	if (type === 4) {
		const select = /** @type {HTMLSelectElement} */ (nativeEl);
		const options = [...select.options].filter((o) => o.value !== '').map((o) => ({ id: o.value, label: o.textContent?.trim() ?? '' }));
		return { type, options, selected: select.value ? [select.value] : [], disabled: select.disabled, mandatory };
	}

	const boxes = [...nativeEl.querySelectorAll('input[type="checkbox"]')];
	const options = boxes.map((box) => ({ id: box.value, label: box.closest('label')?.textContent?.trim() ?? '' }));
	const selected = boxes.filter((box) => box.checked).map((box) => box.value);
	// A disabled <ul> itself is never set by the legacy (only its own disabled attribute would be,
	// which nothing writes) — the read-only signal for type 5 is every inner checkbox being disabled.
	const disabled = boxes.length > 0 && boxes.every((box) => box.disabled);

	return { type, options, selected, disabled, mandatory };
}

/**
 * Writes a new selection back onto the native element and fires the `change` the legacy save path
 * listens for, then returns what the native element actually ended up holding — the DOM stays
 * authoritative even for this optimistic update (e.g. a stale id that no longer matches an
 * `<option>` silently resets a `<select>` to `''`).
 *
 * @param {HTMLElement} nativeEl
 * @param {string[]} nextIds ids to select, already in configuration order
 * @returns {string[]} the selection actually applied
 */
export function writeNativeField(nativeEl, nextIds) {
	const type = nativeFieldType(nativeEl);
	if (type === null) return [];

	if (type === 4) {
		const select = /** @type {HTMLSelectElement} */ (nativeEl);
		select.value = nextIds[0] ?? '';
		select.dispatchEvent(new Event('change', { bubbles: true }));
		return select.value ? [select.value] : [];
	}

	// __attributeChanged's checkbox branch requires evt.target to BE one of the `chk…` checkboxes
	// (visitStep3-statement.js:3257) and recomputes the value from `closest('ul').find(':checked')`,
	// so every box must be set first and the dispatch must originate on one of them — never on the
	// <ul>, which would fall through to the wrong branch and save an empty/garbage value.
	const boxes = [...nativeEl.querySelectorAll('input[type="checkbox"]')];
	if (boxes.length === 0) return [];

	const wanted = new Set(nextIds);
	boxes.forEach((box) => {
		box.checked = wanted.has(box.value);
	});

	const source = boxes.find((box) => box.checked) ?? boxes[0];
	source.dispatchEvent(new Event('change', { bubbles: true }));

	return boxes.filter((box) => box.checked).map((box) => box.value);
}

/**
 * Where a floating menu anchored on `nativeEl` should attach.
 *
 * `#vue-overlay-root` is a valid target on the relevé screen in every case this ticket covers —
 * there is no top-layer dialog (`<dialog>` / `md-dialog`) in that screen's DOM. The lookup still
 * checks for one defensively, because a Vuetify overlay attached to `#vue-overlay-root` paints
 * BEHIND the browser top layer and would silently render invisible.
 *
 * @param {HTMLElement} nativeEl
 * @param {Document} doc the legacy iframe document
 * @returns {Element|null}
 */
export function resolveAttachTarget(nativeEl, doc) {
	const dialog = nativeEl.closest?.('dialog');
	if (dialog) return dialog;

	const mdDialog = nativeEl.closest?.('md-dialog');
	if (mdDialog) return mdDialog.querySelector('[slot="content"]') ?? mdDialog;

	return doc.getElementById('vue-overlay-root') ?? doc.body;
}
